import {ReactNode, useId, useRef, useState} from "react";
import {CircleAlertIcon, SearchXIcon} from "lucide-react";
import {PageModel} from "@/api/Paging.ts";
import SearchBar from "@/search/SearchBar.tsx";
import useAsyncSearch from "@/search/useAsyncSearch.ts";
import {Popover, PopoverAnchor, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {cn} from "@/lib/utils.ts";

export type SearchItemContext<T> = {
    query: string;
    select: () => void;
    selecting: boolean;
    item: T;
};

export function HighlightedSearchText({text, query}: {text: string; query: string}) {
    const normalizedQuery = query.trim();
    const matchIndex = text.toLocaleLowerCase("de-DE").indexOf(normalizedQuery.toLocaleLowerCase("de-DE"));
    if (matchIndex < 0 || !normalizedQuery) return text;

    const matchEnd = matchIndex + normalizedQuery.length;
    return <>
        {text.slice(0, matchIndex)}
        <span className="text-muted-foreground">{text.slice(matchIndex, matchEnd)}</span>
        {text.slice(matchEnd)}
    </>;
}

export default function SearchPopover<T>({search, getKey, renderItem, onSelect, label, placeholder = "Suche",
                                             minQueryLength = 2, debounceMs = 150, requestKey = "", className,
                                             contentClassName, trigger}: {
    search: (query: string, signal: AbortSignal) => Promise<PageModel<T>>;
    getKey: (item: T) => string;
    renderItem: (context: SearchItemContext<T>) => ReactNode;
    onSelect: (item: T) => void | Promise<void>;
    label?: string;
    placeholder?: string;
    minQueryLength?: number;
    debounceMs?: number;
    requestKey?: string | number;
    className?: string;
    contentClassName?: string;
    trigger?: ReactNode;
}) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [selectingKey, setSelectingKey] = useState<string>();
    const [selectionError, setSelectionError] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const inputId = useId();
    const result = useAsyncSearch({query, search, minQueryLength, debounceMs, requestKey});

    const reset = () => {
        setQuery("");
        setOpen(false);
        setSelectingKey(undefined);
        setSelectionError(false);
    };

    const changeQuery = (value: string) => {
        setQuery(value);
        setOpen(Boolean(trigger) || value.trim().length >= minQueryLength);
        setSelectionError(false);
    };

    const select = async (item: T) => {
        const key = getKey(item);
        setSelectingKey(key);
        setSelectionError(false);
        try {
            await onSelect(item);
            reset();
        } catch (reason) {
            console.error(reason);
            setSelectionError(true);
        } finally {
            setSelectingKey(undefined);
        }
    };

    return <div className={cn("w-full", className)}>
        {label && <label htmlFor={inputId} className="mb-2 block text-sm font-medium">{label}</label>}
        <Popover open={open} onOpenChange={nextOpen => nextOpen ? setOpen(Boolean(trigger) || result.enabled) : reset()}>
            {trigger ? <PopoverTrigger asChild>{trigger}</PopoverTrigger> : <PopoverAnchor asChild>
                <div ref={anchorRef}>
                    <SearchBar id={inputId} value={query} onChange={changeQuery}
                               onFocus={() => result.enabled && setOpen(true)} placeholder={placeholder}/>
                </div>
            </PopoverAnchor>}
            <PopoverContent align="start"
                            className={cn("max-h-[min(24rem,var(--radix-popover-content-available-height))] w-(--radix-popover-trigger-width) min-w-72 overflow-y-auto rounded-md p-0", contentClassName)}
                            onOpenAutoFocus={event => {
                                if (!trigger) event.preventDefault();
                            }}
                            onInteractOutside={event => {
                                if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
                            }}>
                {trigger && <div className="border-b p-2">
                    <SearchBar id={inputId} value={query} onChange={changeQuery} placeholder={placeholder}/>
                </div>}
                <SearchPopoverResults result={result} query={query} selectingKey={selectingKey}
                                      selectionError={selectionError} getKey={getKey} renderItem={renderItem}
                                      onSelect={item => void select(item)}/>
            </PopoverContent>
        </Popover>
    </div>;
}

function SearchPopoverResults<T>({result, query, selectingKey, selectionError, getKey, renderItem, onSelect}: {
    result: ReturnType<typeof useAsyncSearch<T>>;
    query: string;
    selectingKey?: string;
    selectionError: boolean;
    getKey: (item: T) => string;
    renderItem: (context: SearchItemContext<T>) => ReactNode;
    onSelect: (item: T) => void;
}) {
    const remaining = result.results
        ? Math.max(0, result.results.page.totalElements - result.results.content.length)
        : 0;

    if (!result.enabled) return <div className="p-4 text-center text-sm text-muted-foreground">
        Suchbegriff eingeben.
    </div>;

    if (result.loading) return <div role="status" aria-label="Suche läuft">
        <SearchResultSkeleton/>
        <SearchResultSkeleton/>
        <SearchResultSkeleton/>
    </div>;

    if (result.error) return <div className="flex items-center justify-center gap-2 p-5 text-destructive" role="alert">
        <CircleAlertIcon className="size-4"/> Suche fehlgeschlagen.
    </div>;

    if (result.results?.page.totalElements === 0) return (
        <div className="flex flex-col items-center gap-1.5 p-6 text-center text-muted-foreground">
            <SearchXIcon className="size-6"/>
            <span>Keine Ergebnisse</span>
            <span className="text-xs">Versuche einen anderen Suchbegriff.</span>
        </div>
    );

    return <div className="divide-y divide-border text-sm">
        {selectionError && <div className="flex items-center justify-center gap-2 p-3 text-destructive" role="alert">
            <CircleAlertIcon className="size-4"/> Auswahl fehlgeschlagen.
        </div>}
        {result.results?.content.map(item => {
            const key = getKey(item);
            return <div key={key}>{renderItem({
                item,
                query,
                selecting: selectingKey === key,
                select: () => onSelect(item),
            })}</div>;
        })}
        {remaining > 0 && <div className="px-3 py-2 text-center text-xs text-muted-foreground">
            Weitere {remaining} {remaining === 1 ? "Ergebnis" : "Ergebnisse"} – Suche präzisieren
        </div>}
    </div>;
}

function SearchResultSkeleton() {
    return <div className="flex items-center gap-3 px-3 py-2.5 last:border-b-0" aria-hidden="true">
        <div className="size-10 shrink-0 animate-pulse rounded-md bg-muted"/>
        <div className="grow space-y-2">
            <div className="h-3.5 w-2/5 animate-pulse rounded bg-muted"/>
            <div className="h-3 w-3/5 animate-pulse rounded bg-muted/70"/>
        </div>
    </div>;
}
